import { useMemo, useState, type MouseEvent } from "react";
import { Box, Menu, MenuItem, Button as MUIButton, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { PatientTimelineResponse } from "@thalamos/aspire-common";

import {
  CreateRioLinkButton,
  LinkToPdsButton,
} from "../CreateLinkButton/CreateLinkButton.js";
import type { ExternalPatientLink } from "../externalPatientLink.js";
import {
  ManualPullButton,
  StartIcon,
} from "../ManualPullButton/ManualPullButton.js";
import { computeSyncBadgeColour, filterPullableLinks } from "./helpers.js";
import { routeFns } from "~/routePaths.js";

export function PatientExternalLinkActions({
  patient,
  externalLinks,
  shouldShowCreateRioLinkButton = false,
  shouldShowLinkToPdsButton = false,
  displayMode = "desktop",
}: {
  patient: PatientTimelineResponse["patient"] | undefined;
  externalLinks: ExternalPatientLink[];
  shouldShowCreateRioLinkButton?: boolean;
  shouldShowLinkToPdsButton?: boolean;
  displayMode?: "desktop" | "mobile";
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const pullableLinks = useMemo(
    () =>
      filterPullableLinks(
        externalLinks,
        shouldShowCreateRioLinkButton,
        shouldShowLinkToPdsButton,
      ),
    [externalLinks, shouldShowCreateRioLinkButton, shouldShowLinkToPdsButton],
  );

  const onlyPullableLink =
    pullableLinks.length === 1 ? pullableLinks[0] : undefined;

  const badgeColor = useMemo(
    () => computeSyncBadgeColour(pullableLinks),
    [pullableLinks],
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Render flat menu items for use inside an existing flyout/menu container
  if (displayMode === "mobile") {
    return (
      <>
        {pullableLinks.map((link) => (
          <ManualPullButton
            key={link.id}
            disabled={link.isInvalid}
            externalSystemType={link.externalSystemType}
            externalSystemDisplayName={link.externalSystemDisplayName}
            dateLastProcessed={link.dateLastProcessed}
            allExternalSystemDisplayNames={externalLinks.map(
              (l) => l.externalSystemDisplayName ?? "",
            )}
            onSync={async () => {
              if (patient && patient.id) {
                navigate(routeFns.patientSyncExternalLink(patient.id, link.id));
              }
            }}
            displayMode="menu-item"
          />
        ))}
        {shouldShowLinkToPdsButton && patient && (
          <LinkToPdsButton patient={patient} displayMode="menu-item" />
        )}
        {shouldShowCreateRioLinkButton && patient && (
          <CreateRioLinkButton patient={patient} displayMode="menu-item" />
        )}
      </>
    );
  }

  function getVisibleExternalLinkActions(links: ExternalPatientLink[]): number {
    const buttonLinks =
      (shouldShowCreateRioLinkButton ? 1 : 0) +
      (shouldShowLinkToPdsButton ? 1 : 0);

    return links.length + buttonLinks;
  }

  const totalDropdownItems = getVisibleExternalLinkActions(pullableLinks);

  const shouldUseDropdown = totalDropdownItems > 1;

  return (
    <>
      {shouldShowLinkToPdsButton && patient && !shouldUseDropdown && (
        <Stack
          direction={"column"}
          sx={{
            gap: "0.5rem",
            marginTop: "0.5rem",
          }}
        >
          <LinkToPdsButton patient={patient} displayMode="button" />
        </Stack>
      )}
      {shouldShowCreateRioLinkButton && patient && !shouldUseDropdown && (
        <Stack
          direction={"column"}
          sx={{
            gap: "0.5rem",
            marginTop: "0.5rem",
          }}
        >
          <CreateRioLinkButton patient={patient} displayMode="button" />
        </Stack>
      )}
      {onlyPullableLink !== undefined && !shouldUseDropdown && (
        <Stack
          direction={"column"}
          sx={{
            gap: "0.5rem",
            marginTop: "0.5rem",
          }}
        >
          <ManualPullButton
            key={JSON.stringify(onlyPullableLink)}
            externalSystemType={onlyPullableLink.externalSystemType}
            externalSystemDisplayName={
              onlyPullableLink.externalSystemDisplayName
            }
            dateLastProcessed={onlyPullableLink.dateLastProcessed}
            allExternalSystemDisplayNames={externalLinks.map(
              (l) => l.externalSystemDisplayName ?? "",
            )}
            onSync={async () => {
              if (patient && patient.id) {
                navigate(
                  routeFns.patientSyncExternalLink(
                    patient.id,
                    onlyPullableLink.id,
                  ),
                );
              }
            }}
            displayMode={"button"}
          />
        </Stack>
      )}
      {/* Will show a dropdown if there are multiple external links */}
      {shouldUseDropdown && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <MUIButton
              sx={{
                whiteSpace: "nowrap",
                fontWeight: 600,
                gap: 1,
                mt: 1,
              }}
              variant="contained"
              onClick={handleClick}
              fullWidth
            >
              <StartIcon synchronizing={false} badgeColor={badgeColor} />
              {t("components.externalPatientLink.manualPullButton.syncPatient")}
            </MUIButton>
          </Box>

          <Menu open={open} anchorEl={anchorEl} onClose={handleClose}>
            {pullableLinks.map((link) => (
              <MenuItem
                key={link.id}
                disableGutters
                disableRipple
                sx={{ p: 0 }}
              >
                <ManualPullButton
                  disabled={link.isInvalid}
                  externalSystemType={link.externalSystemType}
                  externalSystemDisplayName={link.externalSystemDisplayName}
                  dateLastProcessed={link.dateLastProcessed}
                  allExternalSystemDisplayNames={externalLinks.map(
                    (l) => l.externalSystemDisplayName ?? "",
                  )}
                  onSync={async () => {
                    handleClose();
                    if (patient && patient.id) {
                      navigate(
                        routeFns.patientSyncExternalLink(patient.id, link.id),
                      );
                    }
                  }}
                  displayMode={"menu-item"}
                />
              </MenuItem>
            ))}

            {/* Add LinkToPdsButton if it's not linked and PDS is enabled */}
            {shouldShowLinkToPdsButton && patient && (
              <LinkToPdsButton patient={patient} displayMode="menu-item" />
            )}

            {/* Add CreateRioLinkButton if none of the links are pullable */}
            {shouldShowCreateRioLinkButton && patient && (
              <CreateRioLinkButton patient={patient} displayMode="menu-item" />
            )}
          </Menu>
        </>
      )}
    </>
  );
}
